import { arrayWithContents } from "../../../utils"
import { adjustXAxisValue } from "../../resources/utils"

export function lowerBound(data, xTime, timeExtractor, trendDayOffset = 0) {
  let lb = 0,
    ub = data.length - 1

  while (ub > lb) {
    const mid = ((ub + lb) / 2 + 1) | 0 // Round up to ensure lower-bound search converges
    const midDelta = timeExtractor.difference(
      timeExtractor.getObservationTime(data[mid]),
      adjustXAxisValue(xTime, timeExtractor, trendDayOffset),
    )

    if (midDelta <= 0) {
      lb = mid
    } else {
      ub = mid - 1
    }
  }

  return ub
}

export function upperBound(data, xTime, timeExtractor, trendDayOffset = 0) {
  let lb = 0,
    ub = data.length - 1

  while (ub > lb) {
    const mid = ((ub + lb) / 2) | 0
    const midDelta = timeExtractor.difference(
      timeExtractor.getObservationTime(data[mid]),
      adjustXAxisValue(xTime, timeExtractor, trendDayOffset),
    )

    if (midDelta >= 0) {
      ub = mid
    } else {
      lb = mid + 1
    }
  }

  return ub
}

export function downsample(layer, plot_type, x, timeExtractor, window) {
  // We don't want to 'downsample' sample based data as the data are connected to specific Sample entities
  // and that association is best maintained and not misrepresented
  const nonSampleData = layer.data.filter((d) => !d.is_sample)
  if (!arrayWithContents(nonSampleData) || ["rnaseq"].includes(plot_type)) {
    // No data to downsample, move on
    return layer
  }

  const [minTime, maxTime] = x.domain()
  const { trendDayOffset = 0 } = layer
  const dataStart = lowerBound(nonSampleData, minTime, timeExtractor, trendDayOffset),
    dataEnd = upperBound(nonSampleData, maxTime, timeExtractor, trendDayOffset)

  const coords = []
  for (let i = dataStart; i <= dataEnd; ++i) {
    coords.push(x(timeExtractor.getObservationTime(nonSampleData[i])))
  }

  const resultData = layer.data.filter((d) => d.is_sample)

  let wmin = 0,
    wmax = 0,
    w = []

  function flush() {
    if (wmax - wmin < 3) {
      for (let i = wmin; i <= wmax; ++i) {
        resultData.push(nonSampleData[dataStart + i])
      }
    } else {
      w.length = 0
      for (let i = wmin; i <= wmax; ++i) {
        w.push(nonSampleData[dataStart + i])
      }
      w.sort((a, b) => a.value - b.value)
      resultData.push(w[0]) // minimum
      resultData.push(w[w.length >> 1]) // median
      resultData.push(w[w.length - 1]) // maximum
    }

    wmax = wmin = wmax + 1
  }

  while (wmax < coords.length - 1) {
    if (coords[wmax + 1] < coords[wmin] + window) {
      ++wmax
    } else {
      flush()
    }
  }
  flush()

  // Re-sort because the min/med/max thinning will (locally) disorder points
  resultData.sort((a, b) =>
    timeExtractor.difference(timeExtractor.getObservationTime(a), timeExtractor.getObservationTime(b)),
  )

  return {
    ...layer,
    data: resultData,
  }
}

export const set_bounds = (value) => {
  /*
   * Canvas seems to have trouble with y-coordinate values that are too extreme (a value of -1.35e+22 was our starting
   * place to test the extreme). So much so that a trend containing an extremely negative coordinate would not appear
   * on the canvas at all (even if all other coordinates should appear on the graph). I stepped the max_extreme down
   * one decimal place at a time to see what the limit actually was. Values -1e9 <= value <= 1e9 seem to work again.
   * But given that SVG plotting code already instituted a max coordinate range of -5000 to 5000 let's do the same here.
   *
   * Generalizing a -5000 to 5000 allowed range for y-coordinate drawing into a function here
   * */
  const max_extreme = 5000
  if (value >= max_extreme) {
    return max_extreme
  } else if (value <= -max_extreme) {
    return -max_extreme
  }
  return value
}
